import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import useSEO from '@/hooks/useSEO';
import { Button } from '@/components/ui/button';
import { ListChecks, CheckCircle2, Globe2, FileSignature, KeyRound, Scale, ArrowRight, Sparkles } from 'lucide-react';

/**
 * Sales-pitch landing page for the "Tracked Transactions" trust pillar.
 */
export default function WhyTrackedTransactionsPage() {
  const { language } = useLanguage();
  const es = language === 'es';
  const t = (en, esT) => (es ? esT : en);

  useSEO({
    title: t('Tracked Transactions — Buy with Confidence on WakaCasa', 'Compra Acompañada — Compra con Confianza en WakaCasa'),
    description: t(
      'Every step of your Venezuelan property purchase — tracked. Built for the diaspora from Miami, Madrid or Buenos Aires. Lawyer role + final PDF audit trail included.',
      'Cada paso de tu compra en Venezuela — acompañado. Hecho para la diáspora desde Miami, Madrid o Buenos Aires. Rol de abogado + PDF final con trazabilidad.',
    ),
    canonical: '/why/tracked-transactions',
    image: '/sales/transactions-list.jpeg',
  });

  const steps = [
    {
      icon: FileSignature,
      title: t('1. Open the transaction', '1. Abre la transacción'),
      body: t(
        'Once buyer and agent agree, WakaCasa opens a private dashboard with both parties + an optional lawyer.',
        'Una vez que comprador y agente acuerdan, WakaCasa abre un panel privado para ambas partes + abogado opcional.',
      ),
    },
    {
      icon: ListChecks,
      title: t('2. Track every milestone', '2. Sigue cada hito'),
      body: t(
        'Offer, deposit, due diligence, POA, registry visit, key handover — every checkpoint is logged with date, owner and the supporting document.',
        'Oferta, reserva, due diligence, poderes, visita al registro, entrega de llaves — cada checkpoint queda registrado con fecha, responsable y el documento de soporte.',
      ),
    },
    {
      icon: KeyRound,
      title: t('3. Close with full audit trail', '3. Cierra con trazabilidad completa'),
      body: t(
        'When keys change hands, both sides leave with a downloadable PDF showing every step + every document. Indispensable for diaspora buyers.',
        'Cuando se entregan las llaves, ambas partes salen con un PDF descargable que muestra cada paso + cada documento. Indispensable para compradores de la diáspora.',
      ),
    },
  ];

  const bullets = [
    t('No more "where are we in the process?" emails — the dashboard always knows.', 'Adiós a los correos de "¿en qué vamos?" — el panel siempre lo sabe.'),
    t('Built for buyers abroad — every step visible from anywhere, in EN or ES.', 'Pensado para compradores en el exterior — cada paso visible desde cualquier país, en EN o ES.'),
    t('Optional lawyer role with sign-off on legal milestones.', 'Rol opcional de abogado con firma en hitos legales.'),
    t('Document versioning: nobody can "swap" a contract halfway.', 'Versionado de documentos: nadie puede "cambiar" un contrato a mitad de camino.'),
    t('Automatic timeline reminders if a step stalls more than 7 days.', 'Recordatorios automáticos si un paso se atasca más de 7 días.'),
    t('Final PDF audit trail you can hand to the notary, the bank, or your tax advisor.', 'PDF final con la trazabilidad para entregar al notario, al banco o a tu asesor fiscal.'),
  ];

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="why-tracked-transactions-page">
      {/* HERO */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="flex items-center gap-2 mb-5">
            <span className="inline-flex items-center gap-2 bg-[#D4AF37]/10 text-[#D4AF37] px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.18em]">
              <Sparkles className="w-3.5 h-3.5" />
              {t('Tracked Transactions', 'Compra Acompañada')}
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.1] mb-5">
            {t('Every step. ', 'Cada paso. ')}
            <span className="text-[#D4AF37]">{t('Tracked.', 'Acompañado.')}</span>
          </h1>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl leading-relaxed mb-8">
            {t(
              'Buying property in Venezuela from abroad — or from here — has always meant trusting people with your money before paperwork is even drafted. WakaCasa\'s transaction dashboard makes every milestone visible, dated, and signed-off.',
              'Comprar una propiedad en Venezuela desde el exterior —o desde aquí— siempre implicó confiar plata en personas antes de que se redactara el primer documento. El panel de transacciones de WakaCasa hace cada hito visible, fechado y firmado.',
            )}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/transactions">
              <Button className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] font-semibold h-12 px-7 rounded-xl gap-2" data-testid="why-transactions-cta-primary">
                {t('Open my transactions', 'Abrir mis transacciones')}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/search">
              <Button variant="ghost" className="text-white hover:bg-white/10 h-12 px-6 rounded-xl">
                {t('Find a property first', 'Primero encuentra una propiedad')}
              </Button>
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-6 text-xs text-white/50 font-medium">
            <span>🌎 {t('Built for diaspora buyers', 'Hecho para la diáspora')}</span>
            <span>⚖️ {t('Lawyer role included', 'Rol de abogado incluido')}</span>
            <span>📄 {t('Final PDF audit trail', 'PDF final con trazabilidad')}</span>
          </div>
        </div>
      </section>

      {/* SCREENSHOT + DESCRIPTION */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="uppercase tracking-[0.2em] text-xs font-medium text-[#D4AF37] mb-3">
              {t('The transactions dashboard', 'El panel de transacciones')}
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl text-[#0B192C] font-semibold mb-4">
              {t('Both sides see exactly the same thing.', 'Ambos lados ven exactamente lo mismo.')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t(
                'A shared private dashboard with the buyer, the agent, and (optionally) a lawyer. Every milestone shows who completed it, when, and which document backs it.',
                'Un panel privado compartido entre el comprador, el agente y (opcionalmente) un abogado. Cada hito muestra quién lo completó, cuándo, y qué documento lo respalda.',
              )}
            </p>
            <p className="text-sm text-slate-500 leading-relaxed">
              {t(
                'No more WhatsApp chains, no more "I sent you that file last week". Everything lives in the transaction with full version history.',
                'Adiós a las cadenas de WhatsApp y al "te mandé el archivo la semana pasada". Todo vive en la transacción con historial completo.',
              )}
            </p>
          </div>
          <div className="relative">
            <div className="absolute -inset-3 bg-gradient-to-br from-[#D4AF37]/20 to-transparent rounded-2xl blur-xl" />
            <img
              src="/sales/transactions-list.jpeg"
              alt={t('WakaCasa transactions dashboard', 'Panel de transacciones WakaCasa')}
              className="relative rounded-2xl shadow-2xl border border-slate-200 w-full"
              loading="lazy"
              data-testid="why-transactions-screenshot"
            />
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="bg-white border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
          <p className="uppercase tracking-[0.2em] text-xs font-medium text-[#D4AF37] mb-3 text-center">
            {t('3 simple steps', '3 pasos simples')}
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl text-[#0B192C] font-semibold mb-12 text-center">
            {t('How a tracked transaction flows', 'Cómo fluye una compra acompañada')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-testid={`why-transactions-step-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-[#0B192C] text-[#D4AF37] flex items-center justify-center mb-4">
                  <s.icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading text-base text-[#0B192C] font-semibold mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DIASPORA HIGHLIGHT */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <div className="bg-gradient-to-br from-[#0B192C] to-[#0f2540] text-white rounded-2xl p-8 sm:p-12 border border-[#D4AF37]/20">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 text-[#D4AF37] flex items-center justify-center shrink-0">
              <Globe2 className="w-6 h-6" />
            </div>
            <div>
              <p className="uppercase tracking-[0.2em] text-xs font-medium text-[#D4AF37] mb-2">
                {t('For overseas buyers', 'Para compradores del exterior')}
              </p>
              <h2 className="font-heading text-2xl sm:text-3xl text-white font-semibold mb-3">
                {t(
                  'Buy from Miami, Madrid or Buenos Aires — without flying blind.',
                  'Compra desde Miami, Madrid o Buenos Aires — sin volar a ciegas.',
                )}
              </h2>
              <p className="text-white/70 leading-relaxed">
                {t(
                  'POA notarisation, registry visits, and currency conversion all happen at home. The transaction dashboard is your live, time-zone-friendly window into everything happening in Venezuela.',
                  'Notarización de poderes, visitas al registro y conversión de divisas suceden en casa. El panel de transacciones es tu ventana en vivo, amigable con tu zona horaria, a todo lo que ocurre en Venezuela.',
                )}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BULLETS */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <p className="uppercase tracking-[0.2em] text-xs font-medium text-[#D4AF37] mb-3">
          {t('What you get', 'Qué obtienes')}
        </p>
        <h2 className="font-heading text-2xl sm:text-3xl text-[#0B192C] font-semibold mb-8">
          {t('Why every WakaCasa deal should be tracked', '¿Por qué cada operación WakaCasa debe ser acompañada?')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {bullets.map((b, i) => (
            <div key={i} className="flex items-start gap-3 bg-white border border-slate-100 rounded-xl p-4">
              <CheckCircle2 className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" />
              <span className="text-sm text-slate-700 leading-relaxed">{b}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CTA FOOTER */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
          <Scale className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold mb-4">
            {t('Open a tracked transaction today', 'Abre una transacción acompañada hoy')}
          </h2>
          <p className="text-white/70 leading-relaxed mb-8 max-w-xl mx-auto">
            {t(
              'No fee to open — pay only on close. Available for every property and every agent on WakaCasa.',
              'Sin costo de apertura — paga solo al cierre. Disponible para cada propiedad y cada agente en WakaCasa.',
            )}
          </p>
          <Link to="/transactions">
            <Button className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] font-semibold h-12 px-8 rounded-xl gap-2" data-testid="why-transactions-cta-footer">
              {t('Open my transactions', 'Abrir mis transacciones')}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
